import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import CommandCenter from './components/CommandCenter'
import Icon from './components/Icon'
import { useAppUpdate } from './hooks/useAppUpdate'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'home' | 'account' | 'admin'

/**
 * The closing brief, published as an Artifact.
 *
 * It lives outside this app, so it belongs to no tab and the bar is where it
 * goes. Note it is private to the publishing account until it is deliberately
 * shared — this link is a shortcut for the owner, not a handout for the lender.
 */
const CLOSING_BRIEF_URL = 'https://claude.ai/code/artifact/74a92039-9b91-473d-bc20-88c87d4a934f'

/**
 * Account and sign-out, as one control.
 *
 * Two permanent buttons for things touched once a month crowded a bar whose job
 * is to say where you are. One opens; the rare things live inside it.
 */
function AccountMenu({
  user, onAccount, onSignOut, current,
}: { user: User; onAccount: () => void; onSignOut: () => Promise<void>; current: boolean }) {
  const [open, setOpen] = useState(false)
  const update = useAppUpdate()

  useEffect(() => {
    if (!open) return
    // Any click that is not on the menu closes it — including one on the button
    // that opened it, which is what makes a second press feel like a toggle.
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.acct')) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('click', close)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', esc) }
  }, [open])

  return (
    <span className="acct">
      {/* Named "Account", not by the display name. A control whose accessible
          name is whatever the user happens to be called is one a screen reader
          cannot describe and a test cannot find. */}
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        aria-current={current}
        className="acct-btn"
      >
        <Icon name="person" size={18} />
        <span className="acct-name">{user.displayName}</span>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="acct-menu" role="menu">
          <button role="menuitem" onClick={() => { setOpen(false); onAccount() }}>
            <Icon name="key" size={16} /> Account &amp; passkeys
          </button>
          {/*
           * Always here, not only when an update is waiting.
           * A control that appears only when it is needed is a control nobody
           * knows exists — and "is this the latest?" is asked far more often
           * than an update actually lands.
           */}
          <button role="menuitem" onClick={() => { setOpen(false); update.reload() }}>
            <Icon name="chevron" size={16} />
            {update.available ? 'Update — a new version is ready' : 'Check for updates'}
          </button>
          <button role="menuitem" className="acct-out" onClick={() => { setOpen(false); void onSignOut() }}>
            <Icon name="back" size={16} /> {t('app.signOut')}
          </button>
        </div>
      )}
    </span>
  )
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />

      {/*
        * The masthead.
        *
        * Two names, deliberately: The Ridge is what this is called, and Monte
        * Carmelo is what the executed LOI and Option Agreement call it. Anyone
        * matching this screen against a document needs to see both.
        */}
      <header className="rg-head">
        <div className="rg-head-in">
          <div className="rg-id">
            <button
              className="rg-mark"
              onClick={() => setTab('home')}
              title={tab === 'home' ? 'You are here' : 'Back to the command center'}
              aria-label="The Ridge — command center"
            >
              <Icon name="ridge" size={20} />
            </button>
            <div style={{ minWidth: 0 }}>
              <div className="rg-crumb">
                <strong>{health?.app ?? 'The Ridge'}</strong>
                <span aria-hidden="true">/</span>
                <span>Monte Carmelo</span>
                {/* Beside the name, not in the footer. "Which build am I looking
                    at" is asked while reading the page, and a stale bundle is
                    invisible until you can see the number. */}
                {health && (
                  <span className="brand-ver" title={`${health.commit} · ${health.environment}`}>
                    v{health.version}
                  </span>
                )}
              </div>
              <p className="rg-sub">Refinance + acquisition command center</p>
            </div>
          </div>

          {user && (
            <div className="rg-headtools">
              {/*
                * The command line is on the page, not behind this button. This
                * focuses it, so the affordance is where the eye is and the
                * input stays where the reading is.
                */}
              <button
                type="button"
                className="btn-s"
                onClick={() => window.dispatchEvent(new Event('ridge:ask'))}
              >
                <Icon name="chart" size={14} /> {t('cc.ask')}
              </button>
            {/* An anchor, not a button: it is a URL, so middle-click, long-press
                and "copy link" all have to work — which is most of why you open
                it, since the point of the brief is sending it to someone. */}
            <a
              className="navlink"
              href={CLOSING_BRIEF_URL}
              target="_blank"
              rel="noreferrer"
              title="Opens the published closing brief in a new tab"
            >
              <Icon name="external" size={16} />
              {t('app.brief')}
            </a>
            {/* Capability-driven, so the tab is absent rather than present-and-denied. */}
            {user.capabilities.includes('admin.access') && (
              <button onClick={() => setTab('admin')} aria-current={tab === 'admin'}>{t('admin.title')}</button>
            )}
            <AccountMenu
              user={user}
              onAccount={() => setTab('account')}
              onSignOut={async () => { await signOut(); await refresh() }}
              current={tab === 'account'}
            />
            </div>
          )}
        </div>
      </header>

      <main>
        {!user && <div className="page"><SignIn onSignedIn={refresh} /></div>}
        {/* Home IS the Command Center. Signing in and then having to navigate to
            the only thing this app is for would be a menu in front of a door. */}
        {user && tab === 'home' && <CommandCenter environment={health?.environment} />}
        {user && tab === 'account' && <div className="page"><Account user={user} /></div>}
        {user && tab === 'admin' && <div className="page"><Admin /></div>}
      </main>

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Below the fold, not in the way. For an app that is not on a store yet. */}
      <InstallPrompt />

      <footer className="muted small">
        {/* The version moved up beside the name; the commit stays here, because
            it is what you quote when something is wrong and it is too long to
            sit in a header. */}
        {health ? `${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}
